import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
} from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import { routes } from '../../lib/routes';
import { Plus, Trash2, Route } from 'lucide-react';
import {
  useRoutingRules,
  useDeleteRoutingRule,
} from '../../hooks/useOrgIntegrations';
import { useMeSpaces } from '../../hooks/useMeSpaces';
import { useProviders } from '../../hooks/useIntegrations';
import { formatRelativeTime } from '../activity/utils';
import {
  isDynamicRule,
  normalizeFilterFields,
  type RoutingRule,
} from '../../types/integrations';

/** Human label for a dynamic rule's resolved-at-event-time target. */
function dynamicTargetLabel(rule: RoutingRule): string {
  switch (rule.dynamicTarget?.kind) {
    case 'sender-personal-space':
      return "DM sender's personal space";
    default:
      return 'Dynamic target';
  }
}

interface RoutingRulesTabProps {
  integrationId: string;
  providerName: string;
}

export default function RoutingRulesTab({
  integrationId,
  providerName,
}: RoutingRulesTabProps) {
  const { data: rules, isLoading } = useRoutingRules(integrationId);
  const { data: spaces } = useMeSpaces();
  const { data: providers } = useProviders();
  const deleteRule = useDeleteRoutingRule(integrationId);
  const { success, error: showError } = useToast();

  const [searchParams] = useSearchParams();
  const selectedRuleId = searchParams.get('selRule');

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    rule: RoutingRule | null;
  }>({ isOpen: false, rule: null });

  const spaceNameById = useMemo(() => {
    const map = new Map<string, string>();
    (spaces?.items ?? []).forEach((s) => map.set(s.spaceId, s.name));
    return map;
  }, [spaces]);

  const provider = providers?.find((p) => p.name === providerName);

  const handleDelete = async () => {
    if (!deleteModal.rule) return;
    try {
      await deleteRule.mutateAsync(deleteModal.rule.ruleId);
      success('Rule deleted');
      setDeleteModal({ isOpen: false, rule: null });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete rule');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const rulesList = rules ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Routing rules</h2>
          <p className="text-sm text-muted-foreground">
            Each rule forwards matching events from this integration into a
            specific space. Events that don&rsquo;t match any rule are not
            forwarded.
          </p>
        </div>
        <Link to={routes.console.org.integrationRuleNew(integrationId)}>
          <Button size="sm">
            <Plus className="h-4 w-4 mr-1" />
            New rule
          </Button>
        </Link>
      </div>

      {rulesList.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground mb-3">
              No rules yet. Inbound events from this integration won&rsquo;t be
              forwarded to any space.
            </p>
            <Link to={routes.console.org.integrationRuleNew(integrationId)}>
              <Button>
                <Plus className="h-4 w-4 mr-1" />
                Add your first routing rule
              </Button>
            </Link>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {rulesList.map((rule) => (
            <RoutingRuleCard
              key={rule.ruleId}
              rule={rule}
              spaceName={spaceNameById.get(rule.targetSpaceId)}
              selected={rule.ruleId === selectedRuleId}
              onDelete={() => setDeleteModal({ isOpen: true, rule })}
            />
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, rule: null })}
        onConfirm={handleDelete}
        title="Delete routing rule"
        message={`Delete this rule? Events matching its pattern will stop being forwarded${
          deleteModal.rule
            ? isDynamicRule(deleteModal.rule)
              ? " to the sender's personal space"
              : ` to ${spaceNameById.get(deleteModal.rule.targetSpaceId) ?? 'the target space'}`
            : ''
        }. This cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteRule.isPending}
      />

      {/* Surfaces what fields the provider exposes so admins building rules
          know what's available. Tucked at the bottom so it doesn't compete
          with the rules list itself. */}
      {provider &&
        provider.filterFields &&
        provider.filterFields.length > 0 && (
          <details className="text-xs text-muted-foreground mt-6">
            <summary className="cursor-pointer">
              Routable fields for {provider.displayName}
            </summary>
            <ul className="mt-2 space-y-1 font-mono">
              {normalizeFilterFields(provider.filterFields).map((f) => (
                <li key={f.path}>
                  <span className="text-foreground">{f.path}</span>{' '}
                  <span className="opacity-70">({f.type})</span>
                  {f.description ? ` — ${f.description}` : ''}
                </li>
              ))}
            </ul>
          </details>
        )}
    </div>
  );
}

interface RoutingRuleCardProps {
  rule: RoutingRule;
  spaceName?: string;
  selected?: boolean;
  onDelete: () => void;
}

function RoutingRuleCard({
  rule,
  spaceName,
  selected,
  onDelete,
}: RoutingRuleCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  // Bring the deep-linked rule into view when arriving from an event row.
  useEffect(() => {
    if (selected && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selected]);

  const dynamic = isDynamicRule(rule);

  const pinned = Array.isArray(
    (rule.pattern as Record<string, unknown>).eventType,
  )
    ? ((rule.pattern as Record<string, unknown>).eventType as string[])
    : [];

  return (
    <div
      ref={cardRef}
      className={`rounded-md border bg-card p-4 transition-colors ${
        selected
          ? 'border-blue-500 ring-2 ring-blue-500/40 dark:border-blue-400'
          : ''
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
              <Route className="h-3.5 w-3.5 text-muted-foreground" />
              {dynamic ? dynamicTargetLabel(rule) : (spaceName ?? rule.targetSpaceId)}
            </span>
            {dynamic && <Badge variant="purple">Dynamic</Badge>}
            <Badge variant={rule.status === 'active' ? 'success' : 'gray'}>
              {rule.status}
            </Badge>
            <span className="text-xs text-muted-foreground">
              priority {rule.priority}
            </span>
          </div>
          {rule.description && (
            <p className="text-sm text-foreground mt-2">{rule.description}</p>
          )}
          {pinned.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1 text-xs">
              <span className="text-muted-foreground">pinned:</span>
              {pinned.map((e) => (
                <Badge key={e} variant="info" className="font-mono">
                  {e}
                </Badge>
              ))}
            </div>
          )}
          <details className="mt-2 text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Pattern
            </summary>
            <pre className="mt-1 rounded-md border bg-muted/30 p-2 text-xs overflow-x-auto">
              {JSON.stringify(rule.pattern, null, 2)}
            </pre>
          </details>
          <p className="text-xs text-muted-foreground mt-2">
            Created {formatRelativeTime(rule.createdAt)}
          </p>
        </div>
        <button
          type="button"
          onClick={onDelete}
          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          aria-label="Delete rule"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
